import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Split, X, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SplitAlertBanner — Round 96z (Jul 2026).
 *
 * Top-of-page amber banner surfacing upcoming stock splits in the
 * user's portfolio + watchlist. Pairs 1-for-1 with the bell alert
 * stream:
 *
 *   • Banner shows for every "visible" alert (visible = not removed
 *     AND banner_dismissed_until is null or in the past).
 *   • Dismiss  → banner hides for 3 days, alert stays in the bell.
 *   • Remove   → alert vanishes from both banner and bell.
 *
 * Renders on the dashboard + portfolio pages. Auto-refetches every
 * time the component mounts (i.e. every entry to those pages).
 *
 * If there are multiple visible alerts, the most-imminent ex-date is
 * headlined and the rest are compacted into a "+N more" chip that
 * expands to a list.
 */
export default function SplitAlertBanner() {
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAlerts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/alerts/splits`, { withCredentials: true });
      const visible = (r.data?.alerts || []).filter((a) => a.visible);
      setAlerts(visible);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const dismiss = async (id) => {
    try {
      await axios.post(`${API}/alerts/splits/${id}/dismiss`, {}, { withCredentials: true });
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch { /* silent */ }
  };

  const remove = async (id) => {
    try {
      await axios.post(`${API}/alerts/splits/${id}/remove`, {}, { withCredentials: true });
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch { /* silent */ }
  };

  if (loading || alerts.length === 0) return null;

  const primary = alerts[0];
  const more = alerts.slice(1);

  const openTicker = (ticker) => {
    // Route to dashboard with the ticker query param so StockModal opens.
    navigate(`/dashboard?ticker=${ticker}`);
  };

  return (
    <div
      className="w-full bg-amber-500/10 border-b border-amber-500/40 text-amber-900 dark:text-amber-100"
      data-testid="split-alert-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-wrap items-center gap-3 text-sm">
        <Split className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
        <span className="font-semibold">
          Upcoming split in your holdings:
        </span>
        <button
          type="button"
          onClick={() => openTicker(primary.ticker)}
          className="font-mono font-bold hover:underline"
          data-testid={`split-alert-ticker-${primary.ticker}`}
        >
          {primary.ticker}
        </button>
        <span className="font-mono text-amber-700 dark:text-amber-300">{primary.display_ratio}</span>
        <span className="text-amber-800/80 dark:text-amber-200/80">
          on {primary.execution_date}
        </span>
        {more.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-[11px] font-semibold px-2 py-0.5"
            data-testid="split-alert-more-toggle"
          >
            {expanded ? 'hide' : `+${more.length} more`}
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => dismiss(primary.id)}
            className="flex items-center gap-1 rounded-md border border-amber-500/40 hover:bg-amber-500/15 text-[11px] font-semibold px-2 py-1 transition-colors"
            data-testid="split-alert-dismiss"
            title="Hide this banner for 3 days. Alert stays in the bell."
          >
            <X className="h-3 w-3" aria-hidden="true" />
            Dismiss 3d
          </button>
          <button
            type="button"
            onClick={() => remove(primary.id)}
            className="flex items-center gap-1 rounded-md border border-rose-500/40 text-rose-700 dark:text-rose-300 hover:bg-rose-500/15 text-[11px] font-semibold px-2 py-1 transition-colors"
            data-testid="split-alert-remove"
            title="Remove this alert entirely. No more banners or bell entries for this split."
          >
            <XCircle className="h-3 w-3" aria-hidden="true" />
            Remove
          </button>
        </div>
      </div>
      {expanded && more.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 pb-3 space-y-1.5 border-t border-amber-500/20 pt-2">
          {more.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`split-alert-row-${a.ticker}`}>
              <button
                type="button"
                onClick={() => openTicker(a.ticker)}
                className="font-mono font-bold hover:underline"
              >
                {a.ticker}
              </button>
              <span className="font-mono text-amber-700 dark:text-amber-300">{a.display_ratio}</span>
              <span className="text-amber-800/80 dark:text-amber-200/80">on {a.execution_date}</span>
              <div className="ml-auto flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => dismiss(a.id)}
                  className="rounded border border-amber-500/40 hover:bg-amber-500/15 text-[10px] font-semibold px-1.5 py-0.5"
                  data-testid={`split-alert-dismiss-${a.ticker}`}
                >
                  Dismiss 3d
                </button>
                <button
                  type="button"
                  onClick={() => remove(a.id)}
                  className="rounded border border-rose-500/40 text-rose-700 dark:text-rose-300 hover:bg-rose-500/15 text-[10px] font-semibold px-1.5 py-0.5"
                  data-testid={`split-alert-remove-${a.ticker}`}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
